import { factory } from '@wix/web-bi-logger';
import { Logger } from '@wix/web-bi-logger/dist/src/types';
import { getWidgetPlacement } from '@wix/wix-chatbot-common/bi';
import {
  contextToBiEvent,
  getChatbotView,
} from '@wix/wix-chatbot-common/browser';

import { clientSdk } from '../../common/ClientSDK';
import { TRootState } from '../../common/reducers';
import { selectConversationId } from '../../common/selectors/chat';
import { selectFieldNodeId } from '../../common/selectors/field';
import {
  selectIntentId,
  selectMainOptionName,
} from '../../common/selectors/meta';
import { selectWidgetSessionId } from '../../common/selectors/widgetSession';
import { helpCenterService } from '../../common/services/helpCenter';
import { store } from '../../common/store';
import { experiments } from '../../experiments/client';
import { createNode } from '..';
import { logger } from '../logger';
import { renderModel } from '../renderModel';
import { getTestVersionPayload } from './testVersionPayload';
import {
  BaseBiEvent,
  BaseStatusBannerBiEvent,
  Bi1051Data,
  Bi1119EventDataRaw,
  Bi1120EventDataRaw,
  Bi1121EventData,
  BiSolutionView,
  ChatbotNotificationReceivedBiEvent,
  ClickOnSolutionBiEvent,
  DefaultBiFields,
  EmptySearchResultsReceivedBiEvent,
  HelpCenterBreadcrumbsScrolledBiEvent,
  SchedulerTimeslotDateSelectedBiEvent,
  SchedulerTimeslotMonthArrowClickedBiEvent,
  SchedulerTimeslotTimeSelectedBiEvent,
  SendBiEventData,
  StatusBannerClickedBiEvent,
  StatusBannerDismissedBiEvent,
  StatusBannerShownBiEvent,
} from './types';

const defaultFields: DefaultBiFields = {
  src: 4,
  bot_id: '717b0e2d-a564-4ef7-b965-481b1983133b',
  provider: 'answers',
  referral_url:
    new URL(window.location.href).searchParams.get('referral_url') ||
    document.referrer,
  lang: renderModel.getLocale(),
  referral: new URL(window.location.href).searchParams.get('referral'),
  parent_url: new URL(window.location.href).searchParams.get('loadPageUrl'),
};

const getContextForBi = () => {
  return clientSdk.getContext(renderModel.getUserId()).then((context) => {
    // TODO: update biEventContext after removing '{}' from types
    if (!context || !Object.keys(context).length) {
      const error = new Error(`Context is empty`);
      logger.log({
        error,
        message: `Context is empty. Context: ${JSON.stringify(context)}`,
        callParams: {
          userId: renderModel.getUserId(),
        },
      });
      return;
    }
    try {
      // log error from contextToBiEvent function:
      // Uncaught (in promise) TypeError: undefined is not an object (evaluating 'page.numLevels')
      return {
        chatbot_view: getChatbotView(context),
        ...contextToBiEvent(context),
      };
    } catch (error) {
      logger.log({
        message: `fail to execute contextToBiEvent, context: ${JSON.stringify(
          context,
        )}`,
        error,
      });
    }
  });
};

const getSessionId = (state: TRootState) => {
  const sessionIdFromState = selectWidgetSessionId(state);
  if (sessionIdFromState) {
    return sessionIdFromState;
  }
  return clientSdk.getWidgetSessionId();
};

const getUserPriorityClass = () => {
  return clientSdk.getUserPriorityClass();
};

const getRuntimeDataForBi = async (): Promise<BaseBiEvent> => {
  const state = store.getState();
  return Promise.all([
    getContextForBi(),
    getSessionId(state),
    getUserPriorityClass(),
  ]).then(([biEventContext, sessionId, userPriorityClass]) => ({
    msid: biEventContext?.context_msid,
    ...biEventContext,
    session_id: sessionId,
    conversation_id: selectConversationId(state),
    main_option_name: selectMainOptionName(state),
    nodeId: selectFieldNodeId(state),
    user_intent: selectIntentId(state),
    priority_class: userPriorityClass,
  }));
};

const placement = getWidgetPlacement(
  helpCenterService.isFullWidth,
  renderModel.isInvokedFromHeader(),
);

const sendBiEvent = (data: SendBiEventData, biFactory: Logger) => {
  getRuntimeDataForBi()
    .then((biData) => {
      biFactory.log({
        ...data,
        ...biData,
      });
    })
    .catch((error) => {
      logger.log({
        message: 'Fail to get runtime data for bi',
        error,
      });
    });
};

const biAns = factory({ endpoint: 'ans_bi' })
  .updateDefaults(defaultFields)
  .logger();

const biChatbot = factory({ endpoint: 'chatbot' })
  .updateDefaults(defaultFields)
  .logger();

interface IBiClickLink {
  link: string;
  isCta: boolean;
  label?: string;
  node_id?: string;
  conversation_id?: string;
}

export const biClickLink = ({
  link,
  label,
  isCta,
  conversation_id,
  node_id,
}: IBiClickLink) => {
  if (isCta) {
    sendBiEvent(
      {
        evid: 574,
        action_url: link,
        call_center_status: null,
        estimated_wait_time: null,
        main_option_id: null,
        solution_time: null,
        solution_title: null,
        sub_option_id: null,
        sub_option_name: null,
        ticketing_status: null,
      },
      biAns,
    );
  }

  sendBiEvent(
    {
      evid: 575,
      action_url: link,
      conversation_id,
      link_name: label,
      link_url: link,
      call_center_status: null,
      estimated_wait_time: null,
      main_option_id: null,
      solution_time: null,
      solution_title: null,
      sub_option_id: null,
      sub_option_name: null,
      ticketing_status: null,
    },
    biAns,
  );

  sendBiEvent(
    {
      evid: 1088,
      conversation_id,
      node_id,
      node_name: null,
      user_intent: null,
      linkUrl: link,
      button_type: isCta ? 'CTA' : 'link',
    },
    biChatbot,
  );
};

export const addWixRecorder = (data: Bi1051Data): void => {
  const label: TProjectName = 'wix-chatbot-client';
  const ts = Date.now() - window.initialTimestamp;

  sendBiEvent(
    {
      evid: 1051,
      conversation_id: data.conversation_id,
      nodeId: data.nodeId,
      ts,
      placement,
      referral: new URL(window.location.href).searchParams.get('referral'),
      parent_url: new URL(window.location.href).searchParams.get('loadPageUrl'),
      ...getTestVersionPayload(),
    },
    biChatbot,
  );

  if (experiments.is('specs.engage.enableGlassbox', 'true')) {
    const script = createNode('script', {
      src:
        'https://static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js',
      async: true,
      crossOrigin: 'anonymous',
      onload() {
        window.wixRecorder.addLabel(label);
        window.wixRecorder.addCustomAttribute(
          'conversation_id',
          data.conversation_id,
        );

        this.onload = null;
      },
    });

    script.setAttribute('data-hook', 'wix-recorder');
    document.body.appendChild(script);
  }
};

export const biSolutionView = ({
  action_url,
  conversation_id,
}: BiSolutionView) => {
  sendBiEvent(
    {
      evid: 573,
      action_url,
      conversation_id,
      solution_time: null,
      solution_title: null,
      sub_option_id: null,
      sub_option_name: null,
      ticketing_status: null,
      call_center_status: null,
      estimated_wait_time: null,
      main_option_id: null,
    },
    biAns,
  );
};

export const biResetButtonClick = ({ conversation_id, nodeId }: Bi1051Data) => {
  sendBiEvent(
    {
      evid: 1054,
      conversation_id,
      placement,
      node_id: nodeId,
      node_name: null,
      previous_node: null,
      previous_node_id: null,
      previous_node_name: null,
      user_intent: null,
      ...getTestVersionPayload(),
    },
    biChatbot,
  );
};

export const biWidgetPresentedContent = (
  data: Omit<Bi1119EventDataRaw, 'evid' | 'placement'>,
) => {
  sendBiEvent(
    {
      evid: 1119,
      ...data,
      placement,
      solution_info: JSON.stringify(data.solution_info),
    },
    biChatbot,
  );
};

export const biEmptySearchResults = (
  data: Omit<EmptySearchResultsReceivedBiEvent, 'evid'>,
) => {
  sendBiEvent(
    {
      evid: 1160,
      ...data,
    },
    biChatbot,
  );
};

export const biWidgetInteractionContent = (
  data: Omit<Bi1120EventDataRaw, 'evid' | 'placement'>,
) => {
  sendBiEvent(
    {
      evid: 1120,
      ...data,
      placement,
      solution_info: JSON.stringify(data.solution_info),
    },
    biChatbot,
  );
};

export const biWidgetInteractionHeader = (
  data: Omit<Bi1120EventDataRaw, 'evid' | 'placement'>,
) => {
  sendBiEvent(
    {
      evid: 1122,
      ...data,
      placement,
      solution_info: JSON.stringify(data.solution_info),
    },
    biChatbot,
  );
};

export const biWidgetPageEngagement = (
  data: Omit<Bi1121EventData, 'evid' | 'placement'>,
) => {
  sendBiEvent(
    {
      evid: 1121,
      ...data,
      placement,
      solution_info: JSON.stringify(data.solution_info),
    },
    biChatbot,
  );
};

export const schedulerTimeslotTimeSelected = (timeslotForBiEvent: string) => {
  const biEventData: SchedulerTimeslotTimeSelectedBiEvent = {
    evid: 1153,
    info: timeslotForBiEvent,
    local_time_zone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const schedulerTimeslotDateSelected = (timeslotsForBiEvent: string) => {
  const biEventData: SchedulerTimeslotDateSelectedBiEvent = {
    evid: 1152,
    info: timeslotsForBiEvent,
    local_time_zone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const schedulerTimeslotMonthArrowClicked = () => {
  const biEventData: SchedulerTimeslotMonthArrowClickedBiEvent = {
    evid: 1151,
    local_time_zone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const statusBannerShown = (data: BaseStatusBannerBiEvent) => {
  const biEventData: StatusBannerShownBiEvent = {
    ...data,
    evid: 1127,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const statusBannerClicked = (data: BaseStatusBannerBiEvent) => {
  const biEventData: StatusBannerClickedBiEvent = {
    ...data,
    evid: 1128,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const statusBannerDismissed = (data: BaseStatusBannerBiEvent) => {
  const biEventData: StatusBannerDismissedBiEvent = {
    ...data,
    evid: 1129,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const clickOnSolution = (
  data: Omit<ClickOnSolutionBiEvent, 'evid' | 'placement'>,
) => {
  const biEventData: ClickOnSolutionBiEvent = {
    ...data,
    placement,
    evid: 1159,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const helpCenterBreadcrumbsScrolled = (
  data: Omit<HelpCenterBreadcrumbsScrolledBiEvent, 'evid'>,
) => {
  const biEventData: HelpCenterBreadcrumbsScrolledBiEvent = {
    ...data,
    evid: 1161,
  };
  sendBiEvent(biEventData, biChatbot);
};

export const chatbotNotificationReceived = (
  data: Omit<ChatbotNotificationReceivedBiEvent, 'evid' | 'placement'>,
) => {
  const biEventData: ChatbotNotificationReceivedBiEvent = {
    ...data,
    placement,
    evid: 1162,
  };
  sendBiEvent(biEventData, biChatbot);
};
